import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { FloatingActionButton } from './FloatingActionButton'
import { QuickScanCamera } from './QuickScanCamera'
import { QuickScanResult } from './QuickScanResult'

interface QuickScanProps {
  studentId: number
  onSuccess?: () => void
}

type ScanState = 'idle' | 'camera' | 'result'

export function QuickScan({ studentId, onSuccess }: QuickScanProps) {
  const navigate = useNavigate()
  const [state, setState] = useState<ScanState>('idle')
  const [capturedImages, setCapturedImages] = useState<string[]>([])
  const [showToast, setShowToast] = useState(false)

  const handleFabClick = () => {
    setState('camera')
  }

  const handleCapture = (imageData: string) => {
    setCapturedImages(prev => [...prev, imageData])
    setState('result')
  }

  const handleConfirm = () => {
    setState('idle')
    setCapturedImages([])
    setShowToast(true)

    // Hide toast after 3 seconds
    setTimeout(() => setShowToast(false), 3000)

    onSuccess?.()
  }

  const handleEdit = (scanId: number) => {
    // Navigate to the scan detail page for full editing
    setState('idle')
    setCapturedImages([])
    navigate(`/parent/student/${studentId}/scans?edit=${scanId}`)
  }

  const handleCancel = () => {
    setState('idle')
    setCapturedImages([])
  }

  const handleAddPage = () => {
    setState('camera')
  }

  return (
    <>
      {/* Floating Action Button - always visible when idle */}
      {state === 'idle' && (
        <FloatingActionButton onClick={handleFabClick} />
      )}

      {/* Camera overlay */}
      {state === 'camera' && (
        <QuickScanCamera
          onCapture={handleCapture}
          onClose={() => {
            if (capturedImages.length > 0) {
              setState('result')
            } else {
              handleCancel()
            }
          }}
          pageNumber={capturedImages.length + 1}
          existingPages={capturedImages.length}
        />
      )}

      {/* Result overlay */}
      {state === 'result' && capturedImages.length > 0 && (
        <QuickScanResult
          images={capturedImages}
          studentId={studentId}
          onConfirm={handleConfirm}
          onEdit={handleEdit}
          onCancel={handleCancel}
          onAddPage={handleAddPage}
        />
      )}

      {/* Success toast */}
      {showToast && (
        <div className="fixed bottom-20 left-1/2 -translate-x-1/2 z-50 bg-green-600 text-white px-6 py-3 rounded-lg shadow-lg flex items-center gap-2 animate-fade-in">
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
          Saved!
        </div>
      )}
    </>
  )
}
